import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import * as React from 'react';

import queryString from 'query-string';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import RoutePaths from '../../../routes/RoutePaths';
import { AsanaService } from '../../../service/asana.service';
import { AuthService } from '../../../service/auth.service';
import { SlackService } from '../../../service/slack.service';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { switchAdmin } from '../../../store/slices/dashboardSlice';
import { fetchIntegrations } from '../../../store/slices/integrationSlice';
import zeplinColor from '../../../theme/Color';
import CustomButton from '../CustomButton/CustomButton';
import CustomCard from '../CustomCard/CustomCard';
import CustomModal from '../CustomModal/CustomModal';
import CustomPopOver from '../CustomPopOver/CustomPopOver';
import { LightTooltip } from '../CustomTooltip/CustomTooltip';
import SidebarData from './Sidebar-data';
import styles from './Sidebar.module.css';
import { usePermissions } from '../../../customHook/usePermissions';

const Sidebar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { integrations, loading: integrationLoading } = useAppSelector(
    (state) => state.integration,
  );
  const { adminConsole: admin } = useAppSelector((state) => state.dashboard);
  const { hasModuleAccess, hasAction, hasFullAccess } = usePermissions();
  const visibleMainItems = SidebarData.main.filter((item) => hasModuleAccess(item.permissionKey));
  const canViewSlack =
    hasFullAccess('Dashboard') ||
    hasAction('Dashboard', 'Overview', 'view') ||
    hasAction('Dashboard', 'Activity', 'view') ||
    hasFullAccess('Clients') ||
    hasAction('Clients', 'Communication', 'view');
  const visibleQuickLinks = SidebarData.quickLinks.filter(
    (item) => item.title !== 'Slack' || canViewSlack,
  );
  const asanaToken = localStorage.getItem('asana-token') || '';
  const captiv8 = sessionStorage.getItem('captiv8') || '';
  const token = sessionStorage.getItem('user-token') || '';
  const isMSLogin = sessionStorage.getItem('ms_login') || '';
  let accessToken: any = {};
  if (token) {
    accessToken = JSON.parse(token);
  }
  const [IsSlackConnected, setIsSlackConnected] = React.useState(
    sessionStorage.getItem('isSlackConnected') === 'true',
  );
  const [asanaConnected, setAsanaConnected] = React.useState(asanaToken ? true : false);
  const [sfToken, setSfToken] = React.useState(sessionStorage.getItem('SFToken') === 'true');
  React.useEffect(() => {
    const handleCustomStorageChange = (e: CustomEvent) => {
      if (e.detail.key === 'isSlackConnected') {
        setIsSlackConnected(e.detail.value === 'true');
      }
      if (e.detail.key === 'SFToken') {
        setSfToken(e.detail.value === 'true');
      }
      if (e.detail.key === 'asana-token') {
        setAsanaConnected(e.detail.value);
      }
    };

    window.addEventListener('sessionStorageChange', handleCustomStorageChange as EventListener);

    return () => {
      window.removeEventListener(
        'sessionStorageChange',
        handleCustomStorageChange as EventListener,
      );
    };
  }, []);

  React.useEffect(() => {
    dispatch(fetchIntegrations());
  }, [dispatch]);

  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [taskAnchorEl, setTaskAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [selectedTitle, setSelectedTitle] = React.useState<string>('');
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const envMode = process.env.REACT_APP_ENV === 'prod' ? 'prod' : 'dev';

  const integrationItems = React.useMemo(() => {
    const fixedIntegrations = ['asana', 'zoominfo'];
    return (integrations || [])
      .map((integration) => {
        const config = integration?.[envMode];
        const homeUrl = (config?.homeUrl || '').trim();
        return {
          title: integration?.name || 'Integration',
          path: homeUrl,
          icon: integration?.iconUrl || '/assets/client.svg',
          isEnabled: Boolean(config?.isEnabled),
        };
      })
      .filter(
        (item) =>
          item.isEnabled && item.path && !fixedIntegrations.includes(item.title.toLowerCase()),
      );
  }, [integrations, envMode]);
  const signOut = async () => {
    setLoading(true);
    try {
      const res = await AuthService.logout({
        accessToken: accessToken?.AccessToken,
        isMSLogin: isMSLogin ? true : false,
      });
      console.log(res);

      if (res?.microsoftLogoutUrl) {
        window.location.href = res?.microsoftLogoutUrl;
      } else {
        navigate(RoutePaths.Root);
      }
      sessionStorage.removeItem('loginExpiry');
      sessionStorage.removeItem('redirect_path');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('user-name');
      sessionStorage.removeItem('user-token');
      sessionStorage.removeItem('asana-token');
      sessionStorage.removeItem('asana-cred');
      sessionStorage.removeItem('asana-expiry');
      sessionStorage.removeItem('currentTab');
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAsanaUnlink = async () => {
    try {
      setLoading(true);
      const asanaCredentials = localStorage.getItem('asana-cred') || '';
      const parsedCreds = asanaCredentials ? JSON.parse(asanaCredentials) : {};
      await AsanaService.asanaLogout(
        { refresh_token: parsedCreds?.refresh_token },
        { 'asana-token': parsedCreds?.access_token },
      );
      localStorage.removeItem('asana-token');
      localStorage.removeItem('asana-expiry');
      localStorage.removeItem('asana-cred');
      navigate(RoutePaths.Asana);
      window.location.reload();
    } catch (err) {
      console.error('Error unlinking Asana:', err);
    } finally {
      setLoading(false);
    }
  };
  const handleSFUnlink = async () => {
    try {
      setLoading(true);
      await SlackService.unlinkSlack({ unlinkSF: true });
      sessionStorage.setItem('SFToken', 'false');
      window.location.reload();
    } catch (err) {
      console.error('Error unlinking Salesforce:', err);
    } finally {
      setLoading(false);
    }
  };
  const handleUnlink = async () => {
    try {
      setLoading(true);
      await SlackService.unlinkSlack({ unlinkSlack: true });
      sessionStorage.setItem('isSlackConnected', 'false');
      // Notify subscribers (this component's own listener + any other) so
      // the connected/disconnected UI state refreshes immediately. Plain
      // sessionStorage.setItem doesn't fire a storage event in the same tab.
      window.dispatchEvent(
        new CustomEvent('sessionStorageChange', {
          detail: { key: 'isSlackConnected', value: 'false' },
        }),
      );
      window.location.reload();
    } catch (err) {
      console.error('Error unlinking Slack:', err);
    } finally {
      setLoading(false);
    }
  };

  const openTask = Boolean(taskAnchorEl);
  const idTask = openTask ? 'simple-popover' : undefined;

  const handleMenuClick = () => {
    if (admin) {
      dispatch(switchAdmin());
    }
  };
  return (
    <Drawer
      sx={{
        width: ' 210px',
        flexShrink: 0,
        height: '100vh',
        '& .MuiDrawer-paper': {
          width: '210px',
          boxSizing: 'border-box',
          backgroundColor: zeplinColor.BasicBlack,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'scroll',
        },
      }}
      variant='permanent'
      anchor='left'
    >
      <Box sx={{ flexGrow: 1 }}>
        <Toolbar
          sx={{
            padding: { xs: '23px 16px', xl: '27px 24px' },
          }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: zeplinColor.BasicBlack,
          }}
        >
          <Link to={RoutePaths.Home}>
            <img
              width={130}
              height={20}
              src={'/assets/new-white-logo-min.png'}
              alt='logo'
              style={{ imageRendering: 'pixelated', filter: 'blur(0px)' }}
            />
          </Link>
        </Toolbar>
        <Divider />
        <List
          sx={{
            display: 'grid',
            rowGap: { xs: '4px', xl: '8px' },
            padding: { xs: '1rem 16px', xl: '1.5rem 20px' },
          }}
        >
          {visibleMainItems.map((val, index) => (
            <NavLink
              key={val.path}
              to={val.path}
              style={({ isActive }) => ({
                ...{ textDecoration: 'none' },
                ...(isActive
                  ? {
                      borderRadius: 14,
                      backgroundColor: zeplinColor.Neutral10,
                    }
                  : {}),
              })}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.activeNavLink}` : styles.navLink
              }
              onClick={() => {
                handleMenuClick();
                sessionStorage.setItem('currentTab', '0');
                sessionStorage.removeItem('dealSelectedFromOverview');
              }}
            >
              <ListItem
                key={val.path}
                disablePadding
                style={{
                  color: zeplinColor.Neutral1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: '12px',
                  }}
                >
                  <ListItemButton
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '12px',
                      padding: '0px',
                    }}
                    disableRipple
                  >
                    <ListItemIcon style={{ minWidth: 0 }}>
                      <img width={28} height={28} src={val.icon} alt='logo' />
                    </ListItemIcon>
                    <Typography variant='responsiveBody3'>{val.title}</Typography>
                  </ListItemButton>
                </Box>
              </ListItem>
            </NavLink>
          ))}
        </List>
        <Divider />
        <List
          sx={{
            display: 'grid',
            rowGap: { xs: '4px', xl: '8px' },
            padding: { xs: '1rem 16px', xl: '1.5rem 20px' },
          }}
        >
          <Typography
            variant='responsiveLabel1'
            sx={{
              color: zeplinColor.Neutral8,
              padding: { xs: '0px 12px 4px 12px', xl: '12px' },
            }}
          >
            Integrations
          </Typography>
          {visibleQuickLinks.map((val) => (
            <Box
              key={val.path}
              sx={{
                ':hover': {
                  borderRadius: 4,
                  backgroundColor: '#1c1c1c',
                },
              }}
              onClick={(e: any) => {
                e.stopPropagation();
                e.preventDefault();
                if (val?.title !== 'Captiv8') {
                  setTaskAnchorEl(e.currentTarget);
                  setSelectedTitle(val.title);
                }
              }}
            >
              <ListItem
                disablePadding
                style={{
                  color: zeplinColor.Neutral1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: '12px',
                  }}
                >
                  <ListItemButton
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '12px',
                      padding: '0px',
                    }}
                    disableRipple
                  >
                    <ListItemIcon style={{ minWidth: 0 }}>
                      <img width={28} height={28} src={val.icon} alt='logo' />
                    </ListItemIcon>
                    <Typography variant='responsiveBody3'>{val.title}</Typography>
                  </ListItemButton>
                  <LightTooltip
                    title={<></>}
                    placement='bottom-start'
                    style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                  >
                    <Box
                      sx={{
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (val?.title !== 'Captiv8') {
                          setTaskAnchorEl(e.currentTarget);
                          setSelectedTitle(val.title);
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: '7px',
                          height: '7px',
                          borderRadius: '50%',
                          backgroundColor:
                            val.title === 'Captiv8' || (val.title === 'Slack' && IsSlackConnected)
                              ? zeplinColor.Success
                              : zeplinColor.AlertInfo,
                        }}
                      ></Box>
                    </Box>
                  </LightTooltip>
                </Box>
              </ListItem>
            </Box>
          ))}
          {integrationLoading
            ? Array.from({ length: 2 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    width: '100%',
                    backgroundColor: zeplinColor.Neutral4,
                  }}
                />
              ))
            : integrationItems.map((val) => (
                <NavLink
                  key={val.path}
                  to={val.path}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={({ isActive }) => ({
                    ...{ textDecoration: 'none' },
                    ...(isActive
                      ? {
                          borderRadius: 14,
                          backgroundColor: zeplinColor.Neutral10,
                        }
                      : {}),
                  })}
                  className={({ isActive }) =>
                    isActive ? `${styles.navLink} ${styles.activeNavLink}` : styles.navLink
                  }
                  onClick={() => {
                    sessionStorage.setItem('currentTab', '0');
                    sessionStorage.removeItem('dealSelectedFromOverview');
                  }}
                >
                  <ListItem
                    key={val.path}
                    disablePadding
                    style={{
                      color: zeplinColor.Neutral1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        padding: '12px',
                      }}
                    >
                      <ListItemButton
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '12px',
                          padding: '0px',
                        }}
                        disableRipple
                      >
                        <ListItemIcon style={{ minWidth: 0 }}>
                          <img
                            width={28}
                            height={28}
                            src={val.icon}
                            alt='logo'
                            style={{ borderRadius: '6px' }}
                          />
                        </ListItemIcon>
                        <Typography variant='responsiveBody3'>{val.title}</Typography>
                      </ListItemButton>
                      <Box
                        sx={{
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img src='/assets/arrow-white.svg' alt='arrow' width={20} height={20} />
                      </Box>
                    </Box>
                  </ListItem>
                </NavLink>
              ))}
        </List>
      </Box>
      <Box>
        <Divider />
        <List
          sx={{
            display: 'grid',
            rowGap: { xs: '4px', xl: '8px' },
            padding: { xs: '1rem 16px', xl: '1.5rem 20px' },
            marginTop: 'auto',
          }}
        >
          {['Log Out'].map((text, index) => (
            <NavLink
              key={'Logout'}
              to={'#'}
              style={({ isActive }) => ({
                ...{ textDecoration: 'none' },
                ...(isActive
                  ? {
                      borderRadius: 14,
                    }
                  : {}),
              })}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.activeNavLink}` : styles.navLink
              }
              onClick={handleOpen}
            >
              <ListItem
                key={text}
                disablePadding
                style={{
                  color: zeplinColor.Neutral1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: '12px',
                  }}
                >
                  <ListItemButton
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '12px',
                      padding: '0px',
                    }}
                    disableRipple
                  >
                    <ListItemIcon style={{ minWidth: 0 }}>
                      <img width={28} height={28} src={'/assets/logout-logo.svg'} alt='logo' />
                    </ListItemIcon>
                    <Typography variant='responsiveBody3'>{text}</Typography>
                  </ListItemButton>
                </Box>
              </ListItem>
            </NavLink>
          ))}
        </List>
      </Box>
      <CustomModal handleClose={handleClose} open={open}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            zIndex: 1000,
          }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: '24px',
              backgroundColor: zeplinColor.Neutral1,
              width: '312px',
              height: '183px',
              boxShadow: 'rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                  Log Out?
                </Typography>
                <Typography variant='body3' color={zeplinColor.BasicBlack}>
                  Are you sure you want to log out?
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: '16px',
                }}
              >
                <Box onClick={handleClose}>
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Neutral1,
                      border: `1px solid ${zeplinColor.BasicBlack}`,
                      cursor: 'pointer',
                    }}
                    aria-label='button'
                    aria-disabled={loading}
                  >
                    <Typography variant='buttonMedium' color={zeplinColor.BasicBlack}>
                      Cancel
                    </Typography>
                  </CustomCard>
                </Box>
                <Box onClick={signOut}>
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.BasicBlack,
                      cursor: 'pointer',
                    }}
                    aria-label='button'
                    aria-disabled={loading}
                  >
                    <Typography
                      variant='buttonMedium'
                      color={zeplinColor.Neutral1}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {loading ? 'Logging out' : 'Log Out'}
                    </Typography>
                  </CustomCard>
                </Box>
              </Box>
            </Box>
          </CustomCard>
        </Box>
      </CustomModal>
      <CustomPopOver
        setAnchorEl={setTaskAnchorEl}
        anchorEl={taskAnchorEl}
        id={idTask}
        sx={{
          borderRadius: '14px',
          boxShadow: 'rgba(0, 0, 0, 0.15)',
          marginTop: '8px',
        }}
      >
        {' '}
        <Box
          sx={{
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            backgroundColor: zeplinColor.BasicBlack,
            width: '403px',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              padding: '8px 10px',
              borderRadius: '30px',
              backgroundColor:
                (selectedTitle === 'Asana' && (asanaConnected || asanaToken)) ||
                (selectedTitle === 'Salesforce' && sfToken) ||
                (selectedTitle === 'Slack' && IsSlackConnected)
                  ? zeplinColor?.Success
                  : zeplinColor?.AlertInfo,
              width: 'fit-content',
            }}
          >
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              {(selectedTitle === 'Asana' && (asanaConnected || asanaToken)) ||
              (selectedTitle === 'Salesforce' && sfToken) ||
              (selectedTitle === 'Slack' && IsSlackConnected) ||
              (selectedTitle === 'Captiv8' && captiv8)
                ? 'Connected'
                : 'Not Connected'}
            </Typography>
          </Box>
          <Divider component='div' sx={{ borderColor: zeplinColor.Neutral10 }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              flexWrap: 'wrap',
            }}
          >
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              {(selectedTitle === 'Asana' && (asanaConnected || asanaToken)) ||
              (selectedTitle === 'Salesforce' && sfToken) ||
              (selectedTitle === 'Slack' && IsSlackConnected) ||
              (selectedTitle === 'Captiv8' && captiv8)
                ? `Disconnecting this integration will disable all related ${selectedTitle} services for Huddle. You can reconnect it at any time`
                : `Click Connect to integrate ${selectedTitle} with Huddle`}
            </Typography>
            {(selectedTitle === 'Asana' && (asanaConnected || asanaToken)) ||
            (selectedTitle === 'Salesforce' && sfToken) ||
            (selectedTitle === 'Slack' && IsSlackConnected) ||
            (selectedTitle === 'Captiv8' && captiv8) ? (
              <Box sx={{ width: '100%' }}>
                <CustomButton
                  disabled={false}
                  variant='outlined'
                  color='customSecondary'
                  style={{
                    color: zeplinColor.Neutral1,
                    borderRadius: '8px',
                    borderWidth: 1,
                    borderColor: zeplinColor.Neutral1,
                    '&:hover': {
                      backgroundColor: loading ? zeplinColor.Neutral8 : zeplinColor.Neutral5,
                      color: loading ? zeplinColor.Neutral1 : zeplinColor.BasicBlack,
                    },
                    '&.MuiButton-root': {
                      padding: '6px 16px',
                    },
                  }}
                  textStyle={{ fontSize: '14px' }}
                  text={loading ? 'Disconnecting...' : 'Disconnect'}
                  icon={''}
                  onClick={() => {
                    selectedTitle === 'Salesforce'
                      ? handleSFUnlink()
                      : selectedTitle === 'Slack'
                        ? handleUnlink()
                        : handleAsanaUnlink();
                  }}
                />
              </Box>
            ) : (
              <Link
                to={
                  selectedTitle === 'Asana' && process.env.REACT_APP_ASANA_LOGIN
                    ? process.env.REACT_APP_ASANA_LOGIN
                    : selectedTitle === 'Salesforce'
                      ? `https://${process.env.REACT_APP_SALESFORCE_URI}/services/oauth2/authorize?client_id=${process.env.REACT_APP_CLIENT_SALESFORCE}&redirect_uri=${process.env.REACT_APP_REDIRECT}&response_type=code`
                      : `https://aamanagementgroup.slack.com/oauth/v2/authorize?${queryString.stringify(
                          {
                            client_id: '1547360932450.9088642262659',
                            scope: '',
                            user_scope:
                              'identify,users:read,channels:read,channels:history,groups:read,groups:write,chat:write,groups:history,im:read,im:history,mpim:read,mpim:history,reactions:read,reactions:write,emoji:read',
                            redirect_uri: `${process.env.REACT_APP_REDIRECT}`,
                            team: 'T01G3ALTED8',
                          },
                        )}`
                }
                onClick={() => {
                  selectedTitle === 'Slack' && sessionStorage.setItem('slackConnecting', 'true');
                }}
                style={{ textDecoration: 'none', width: '100%' }}
              >
                <CustomButton
                  disabled={false}
                  variant='outlined'
                  color='customSecondary'
                  style={{
                    color: zeplinColor.Neutral1,
                    borderRadius: '8px',
                    borderWidth: 1,
                    borderColor: zeplinColor.Neutral1,
                    '&:hover': {
                      backgroundColor: loading ? zeplinColor.Neutral8 : zeplinColor.Neutral5,
                      color: loading ? zeplinColor.Neutral1 : zeplinColor.BasicBlack,
                    },
                    '&.MuiButton-root': {
                      padding: '6px 16px',
                    },
                  }}
                  textStyle={{ fontSize: '14px' }}
                  text={loading ? 'Connecting...' : 'Connect'}
                  icon={''}
                  onClick={() => {}}
                />
              </Link>
            )}
          </Box>
        </Box>
      </CustomPopOver>
    </Drawer>
  );
};

export default Sidebar;
